import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';

const Register = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    role: 'foreman'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/register', {
        username: formData.username,
        password: formData.password,
        full_name: formData.full_name,
        role: formData.role
      });
      
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #004b87 0%, #0066b3 50%, #1a8fcc 100%)',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      padding: '20px',
      position: 'relative',
      overflow: 'hidden',
    },
    backgroundPattern: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      opacity: 0.05,
      backgroundImage: `
        repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.08) 35px, rgba(255,255,255,.08) 70px),
        repeating-linear-gradient(-45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px)
      `,
    },
    decorCircle1: {
      position: 'absolute',
      top: '-10%',
      right: '-5%',
      width: '500px',
      height: '500px',
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(26, 143, 204, 0.3) 0%, transparent 70%)',
      filter: 'blur(60px)',
    },
    decorCircle2: {
      position: 'absolute',
      bottom: '-15%',
      left: '-10%',
      width: '600px',
      height: '600px',
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(0, 75, 135, 0.4) 0%, transparent 70%)',
      filter: 'blur(80px)',
    },
    content: {
      position: 'relative',
      zIndex: 1,
      width: '100%',
      maxWidth: '1200px',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '80px',
      alignItems: 'center',
    },
    branding: {
      color: 'white',
      animation: 'fadeInLeft 0.8s ease-out',
    },
    logoContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      marginBottom: '40px',
    },
    logo: {
      width: '180px',
      height: '180px',
      marginBottom: '25px',
      filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.3))',
    },
    brandTitle: {
      fontSize: '52px',
      fontWeight: '900',
      margin: '0 0 15px 0',
      letterSpacing: '-1.5px',
      textShadow: '3px 3px 6px rgba(0,0,0,0.4)',
      lineHeight: 1.1,
    },
    tagline: {
      fontSize: '20px',
      opacity: 0.95,
      marginBottom: '25px',
      fontWeight: '300',
      letterSpacing: '0.5px',
    },
    divider: {
      height: '3px',
      width: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.3), transparent)',
      marginBottom: '35px',
    },
    systemTitle: {
      fontSize: '26px',
      fontWeight: '700',
      opacity: 0.95,
      letterSpacing: '0.3px',
      marginBottom: '10px',
    },
    systemSubtitle: {
      fontSize: '16px',
      opacity: 0.85,
      fontWeight: '300',
      lineHeight: 1.6,
    },
    card: {
      background: 'rgba(255, 255, 255, 0.98)',
      borderRadius: '24px',
      padding: '50px 45px',
      boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
      backdropFilter: 'blur(20px)',
      animation: 'fadeInUp 0.8s ease-out',
      border: '1px solid rgba(255, 255, 255, 0.2)',
    },
    cardHeader: {
      marginBottom: '35px',
      textAlign: 'center',
    },
    cardTitle: {
      fontSize: '34px',
      fontWeight: '700',
      color: '#004b87',
      marginBottom: '10px',
    },
    cardSubtitle: {
      fontSize: '15px',
      color: '#5a6c7d',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '22px',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '18px',
    },
    label: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#004b87',
      marginLeft: '4px',
    },
    inputWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    inputIcon: {
      position: 'absolute',
      left: '18px',
      width: '22px',
      height: '22px',
      color: '#0066b3',
      opacity: 0.6,
      pointerEvents: 'none',
      zIndex: 1,
    },
    input: {
      width: '100%',
      padding: '16px 18px 16px 52px',
      border: '2px solid #e0e7ef',
      borderRadius: '14px',
      fontSize: '15px',
      fontFamily: 'inherit',
      transition: 'all 0.3s ease',
      outline: 'none',
      backgroundColor: '#fff',
    },
    select: {
      width: '100%',
      padding: '16px 40px 16px 52px',
      border: '2px solid #e0e7ef',
      borderRadius: '14px',
      fontSize: '15px',
      fontFamily: 'inherit',
      transition: 'all 0.3s ease',
      outline: 'none',
      backgroundColor: '#fff',
      appearance: 'none',
      cursor: 'pointer',
    },
    selectWrapper: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    selectArrow: {
      position: 'absolute',
      right: '18px',
      width: '22px',
      height: '22px',
      color: '#0066b3',
      opacity: 0.6,
      pointerEvents: 'none',
    },
    inputFocus: {
      borderColor: '#0066b3',
      boxShadow: '0 0 0 4px rgba(0, 102, 179, 0.1)',
    },
    button: {
      width: '100%',
      padding: '18px',
      background: 'linear-gradient(135deg, #1a8fcc 0%, #0066b3 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '14px',
      fontSize: '17px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      boxShadow: '0 6px 20px rgba(0, 102, 179, 0.35)',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    buttonHover: {
      transform: 'translateY(-3px)',
      boxShadow: '0 10px 30px rgba(0, 102, 179, 0.45)',
      background: 'linear-gradient(135deg, #1a8fcc 0%, #004b87 100%)',
    },
    buttonDisabled: {
      opacity: 0.7,
      cursor: 'not-allowed',
    },
    spinner: {
      width: '20px',
      height: '20px',
      border: '3px solid rgba(255, 255, 255, 0.3)',
      borderTop: '3px solid white',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    },
    alert: {
      padding: '16px 18px',
      borderRadius: '12px',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      backgroundColor: '#fee',
      color: '#c00',
      border: '1px solid #fcc',
    },
    alertIcon: {
      width: '22px',
      height: '22px',
      flexShrink: 0,
    },
    footer: {
      marginTop: '30px',
      textAlign: 'center',
      fontSize: '15px',
      color: '#5a6c7d',
    },
    link: {
      color: '#0066b3',
      textDecoration: 'none',
      fontWeight: '700',
      transition: 'color 0.3s ease',
    },
    infoGrid: {
      display: 'grid',
      gap: '22px',
      marginTop: '45px',
      animation: 'fadeInLeft 1s ease-out',
    },
    infoCard: {
      display: 'flex',
      gap: '22px',
      padding: '28px',
      background: 'rgba(255, 255, 255, 0.12)',
      backdropFilter: 'blur(10px)',
      borderRadius: '18px',
      border: '1px solid rgba(255, 255, 255, 0.25)',
      transition: 'all 0.4s ease',
    },
    infoCardHover: {
      background: 'rgba(255, 255, 255, 0.18)',
      transform: 'translateX(8px)',
    },
    infoIcon: {
      width: '52px',
      height: '52px',
      padding: '13px',
      background: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '14px',
      color: 'white',
      flexShrink: 0,
    },
    infoContent: {
      flex: 1,
    },
    infoTitle: {
      fontSize: '19px',
      fontWeight: '700',
      marginBottom: '8px',
      color: 'white',
    },
    infoText: {
      fontSize: '14px',
      opacity: 0.9,
      lineHeight: 1.6,
      color: 'rgba(255, 255, 255, 0.85)',
    },
  };

  // Add keyframe animations
  React.useEffect(() => {
    const styleSheet = document.styleSheets[0];
    const keyframes = `
      @keyframes fadeInLeft {
        from {
          opacity: 0;
          transform: translateX(-40px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(40px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `;
    
    try {
      styleSheet.insertRule(keyframes, styleSheet.cssRules.length);
    } catch (e) {}
  }, []);

  return (
    <div style={styles.container}>
      <div style={styles.backgroundPattern}></div>
      <div style={styles.decorCircle1}></div>
      <div style={styles.decorCircle2}></div>
      
      <div style={styles.content}>
        <div style={styles.branding}>
          <div style={styles.logoContainer}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px', // space between logo and title
              marginBottom: '25px',
            }}>
              {/* WebP Logo from public folder */}
              <img 
                src={`${process.env.PUBLIC_URL}/ML-Logo.png`}
                alt="Miller & Long Logo" 
                style={{
                  width: '180px',
                  height: '180px',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.3))',
                }} 
              />

              <h1 style={styles.brandTitle}>Miller & Long</h1>
            </div>
          </div>
          <p style={styles.tagline}>Building DC's Future Since 1947</p>
          <div style={styles.divider}></div>
          <h2 style={styles.systemTitle}>Production Management System</h2>
          <p style={styles.systemSubtitle}>Empowering teams with data-driven insights for superior concrete construction</p>

          <div style={styles.infoGrid}>
            <div 
              style={styles.infoCard}
              onMouseEnter={(e) => Object.assign(e.currentTarget.style, styles.infoCardHover)}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <svg style={styles.infoIcon} viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <div style={styles.infoContent}>
                <h4 style={styles.infoTitle}>Secure & Reliable</h4>
                <p style={styles.infoText}>Enterprise-grade security to protect your critical project data</p>
              </div>
            </div>
            <div 
              style={styles.infoCard}
              onMouseEnter={(e) => Object.assign(e.currentTarget.style, styles.infoCardHover)}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <svg style={styles.infoIcon} viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
              </svg>
              <div style={styles.infoContent}>
                <h4 style={styles.infoTitle}>Easy Onboarding</h4>
                <p style={styles.infoText}>Get started in minutes with our intuitive, user-friendly interface</p>
              </div>
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>Create Account</h3>
            <p style={styles.cardSubtitle}>Join your team on the platform</p>
          </div>

          <form onSubmit={handleSubmit} style={styles.form}>
            {error && (
              <div style={styles.alert}>
                <svg style={styles.alertIcon} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Full Name</label>
              <div style={styles.inputWrapper}>
                <svg style={styles.inputIcon} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                  autoFocus
                  style={styles.input}
                  onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e0e7ef';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Username</label>
              <div style={styles.inputWrapper}>
                <svg style={styles.inputIcon} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                </svg>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Choose a username"
                  required
                  style={styles.input}
                  onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e0e7ef';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Password</label>
                <div style={styles.inputWrapper}>
                  <svg style={styles.inputIcon} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Create password"
                    required
                    style={styles.input}
                    onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e0e7ef';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Confirm Password</label>
                <div style={styles.inputWrapper}>
                  <svg style={styles.inputIcon} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm password"
                    required
                    style={styles.input}
                    onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#e0e7ef';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Role</label>
              <div style={styles.selectWrapper}>
                <svg style={styles.inputIcon} viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                </svg>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  style={styles.select}
                  onFocus={(e) => Object.assign(e.target.style, styles.inputFocus)}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e0e7ef';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="foreman">Foreman</option>
                  <option value="estimator">Estimator</option>
                  <option value="admin">Administrator</option>
                </select>
                <svg style={styles.selectArrow} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            <button 
              type="submit" 
              style={{...styles.button, ...(loading && styles.buttonDisabled)}}
              disabled={loading}
              onMouseEnter={(e) => !loading && Object.assign(e.target.style, styles.buttonHover)}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 6px 20px rgba(0, 102, 179, 0.35)';
                e.target.style.background = 'linear-gradient(135deg, #1a8fcc 0%, #0066b3 100%)';
              }}
            >
              {loading ? (
                <>
                  <span style={styles.spinner}></span>
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div style={styles.footer}>
            <p>Already have an account? <Link to="/login" style={styles.link}>Sign In</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;